import { AfterContentInit, Component, ContentChildren, Input, QueryList, Inject } from '@angular/core';
import Enumerable from 'linq';
import { ProjectPerformanceReviewStatuses } from '../../models/pprs/project-performance-review-statuses.enum';
import { ProjectPerformanceReview } from '../../models/pprs/project-performance-review.model';
import { ReviewService } from '../../../core/services/review.service';
import { ToastService } from '../../../core/services/toast-service';
import { ToastMode } from '../../models/toast/toast-mode.enum';
import { ReviewFormSectionComponent } from '../review-form-section/review-form-section.component';
import { Location } from '@angular/common'; //

@Component({
  selector: 'app-review-form',
  templateUrl: './review-form.component.html',
  styleUrl: './review-form.component.css'
})
export class ReviewFormComponent implements AfterContentInit {
  @ContentChildren(ReviewFormSectionComponent) sections!: QueryList<ReviewFormSectionComponent>;

  @Input({ required: true }) title!: string;

  @Input({ required: true }) id!: string;

  @Input({ required: true }) readOnly!: boolean;

  @Input({ required: true }) status!: string;


  constructor(
    private location: Location,
    private reviewService: ReviewService,
    private toastService: ToastService) {
  }

  ngAfterContentInit() {
    const lastIndex = this.sections.length - 1;
    Enumerable.range(0, this.sections.length)
      .forEach(x => this.sections.get(x)
        ?.setIndex(x)
        ?.setLastSection(x == lastIndex));
  }

  public onNextClick = (section: ReviewFormSectionComponent) => {
    // Note: Do not move the following line of code.
    const index = this.index() + 1;

    Enumerable.range(0, this.sections.length - 1)
      .forEach(x => this.sections.get(x)?.setActive(false));

    this.sections.get(index)
      ?.setDisabled(false)
      ?.setShow(true)
      ?.setActive(true);

    this.save(ProjectPerformanceReviewStatuses.InProgress, section);
  }

  public onPreviousClick = (section: ReviewFormSectionComponent) => {
    // Note: Do not move the following line of code.
    const index = this.index() - 1;

    Enumerable.range(0, this.sections.length)
      .forEach(x => this.sections.get(x)?.setActive(false));

    this.sections.get(index)
      ?.setDisabled(false)
      ?.setShow(true)
      ?.setActive(true);

    this.save(ProjectPerformanceReviewStatuses.InProgress, section);
  }

  public onSectionClick = (section: ReviewFormSectionComponent) => {
    const index = section.getIndex();
    Enumerable.from(this.sections.toArray())
      .select((x, i) => ({ section: x, currentIndex: i == index }))
      .forEach(x => {
        x.section
          ?.setShow(x.currentIndex)
          ?.setActive(x.currentIndex);
      });
  }

  public onSaveClick = (section: ReviewFormSectionComponent) => this.save(ProjectPerformanceReviewStatuses.Completed, section);

  public isValid = () => !this.readOnly &&
    !Enumerable.from(this.sections?.toArray() ?? [])
      .select(x => x.isValid())
      .where(x => !x)
      .any();

  private index = () => Enumerable.from(this.sections?.toArray() ?? [])
    .select((x, i) => ({ section: x, index: i }))
    .where(x => x?.section?.active ?? false)
    .firstOrDefault()
    ?.index
    ?? -1;

  private save = (status: string, section: ReviewFormSectionComponent) => {
    if (!section.hasChanges() && status != ProjectPerformanceReviewStatuses.Completed) {
      return;
    }

    if (this.readOnly) {
      return;
    }

    var answers: { id: number, value: string | null, flagged: boolean }[] = [];

    Object.keys(section.formGroup.controls).forEach(key => {
      const value = section.formGroup.get(key)?.value;

      const lineItem = section.lineItems
        .toArray()
        .find(item => item.id == key);

      answers.push({
        id: lineItem?.questionId ?? 0,
        value: value == null ? null : `${value}`,
        flagged: lineItem?.flagged ?? false
      });
    });

    const data: ProjectPerformanceReview = {
      id: this.id,
      status: status,
      answers: answers
    };

    this.reviewService.setReview(data.id, data).subscribe({
      next: _ => {
        this.status = status;
        const isCompleted = ProjectPerformanceReviewStatuses.Completed == status;
        this.readOnly = isCompleted;
        const message = isCompleted
          ? 'Completed'
          : 'Saved';
        Enumerable.from(this.sections.toArray())
          .forEach(x => x.setReadOnly(isCompleted));

        section.formGroup.markAsPristine();
        section.formGroup.markAsUntouched();
        this.toastService.show(message, ToastMode.Success);
      },
      error: (error) => {
        this.toastService.show('Error updating review', ToastMode.Danger);
        console.log(error);
      }
    });
  }

  goBack(): void {
    this.location.back(); // Navigates backward in history
  }
}
